import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router';

import Button from '@/components/ui/button';
import PaymentScreen from '@/components/PaymentScreen';
import { GiftSummaryChip } from '@/components/LandingContext';

import { useOpenBankingMutation } from '@/tsq/mutations';
import { redirectToExternal } from '@/utils/helpers/navigation.helpers';
import { parsePaymentId } from '@/utils/helpers/payment.helpers';
import { formatCurrency } from '@/utils/formatters/currency';
import { miaConsoleLog } from '@/utils/mia-logger';
import { track } from '@/analytics';
import {
  useAmount,
  useChildFirstName,
  useContributionId,
  useGiftImageUrl,
  useGiftCompleted,
  useGiftInviteCode,
  useHydrated,
  useOpenBankingId,
  useOpenBankingURL,
  useSetOpenBankingId,
  useSetOpenBankingURL,
  useShowChildPhoto,
} from '@/store';

/**
 * The payment step (GIFTWEB-26 AC 1 and AC 2). Continuing from the message
 * step lands here, the open-banking payment is created, and the browser is
 * handed to TrueLayer's authorise URL. The screen exists so the jump is not
 * jarring on a slow connection.
 *
 * `redirectToExternal` leaves a history entry, so browser-Back from the bank
 * re-mounts this page. It must not hand the user back to TrueLayer, a TrueLayer 
 * authorise link is single-use, and while the payment is `pending` there is no way
 * to obtain a working one — re-POSTing `open-banking` returns the byte-identical
 * spent token, and `open-banking/restart` answers 409 "Contribution is not in a
 * restartable payment status". So a payment already in flight sends the user to
 * the status screen to find out how it went, rather than to a link that is certain
 * to show TrueLayer's "this link may be broken or expired" page.
 */

type HandoffPhase = 'initiating' | 'error';

function PaymentHandoff() {
  const isHydrated = useHydrated();

  const code = useGiftInviteCode();
  const contributionId = useContributionId();
  const amount = useAmount();
  const childFirstName = useChildFirstName();
  const photoSignedUrl = useGiftImageUrl();
  const showChildPhoto = useShowChildPhoto();
  const giftCompleted = useGiftCompleted();
  const storedAuthoriseUrl = useOpenBankingURL();
  const setOpenBankingURL = useSetOpenBankingURL();
  const storedPaymentId = useOpenBankingId();
  const setOpenBankingId = useSetOpenBankingId();

  const { mutateAsync: initiateOpenBanking } = useOpenBankingMutation();

  // Derived rather than seeded: the store may still be hydrating on the first
  // render, so a stored URL that arrives late must still be honoured. `phase`
  // only holds what this component has explicitly decided.
  const [phase, setPhase] = useState<HandoffPhase | null>(null);
  const effectivePhase: HandoffPhase = phase ?? 'initiating';

  // A payment already in flight is identified by whichever of these survived:
  // the id is stored on handoff, and re-parsed from the URL for sessions that
  // predate it.
  const inFlightPaymentId =
    storedPaymentId || parsePaymentId(storedAuthoriseUrl ?? '');

  // A settled gift has had its payment id released by `completeGift`, so
  // nothing above marks a payment as in flight — while the contribution id,
  // all this page needs to start another one, survives. Landing here again
  // (an old `/payment-method` link, a bookmark, a restored tab) must not
  // quietly hand the contributor back to their bank to pay a second time.
  const alreadyPaid = !!giftCompleted && !inFlightPaymentId;

  // A payment is created at most once per mount. StrictMode double-invokes
  // effects, and a retry must not race the attempt it is replacing.
  const initiating = useRef(false);

  const canInitiate =
    isHydrated && !!code && !!contributionId && !alreadyPaid;

  const handoff = useCallback(async () => {
    if (initiating.current) return;
    initiating.current = true;
    setPhase('initiating');

    try {
      const response = await initiateOpenBanking({ code, uuid: contributionId });
      const authoriseUrl: string | undefined =
        response?.data?.truelayer?.authoriseUrl;

      if (!authoriseUrl) {
        throw new Error('No authorise URL in the open banking response');
      }

      setOpenBankingURL(authoriseUrl);
      // Kept so a re-entry can look the payment up without re-parsing a URL
      // that may not survive a future API change.
      setOpenBankingId(parsePaymentId(authoriseUrl));
      // Before the redirect: once the browser leaves, nothing here runs again.
      track('paymentStarted');
      redirectToExternal(authoriseUrl);
    } catch (error) {
      console.error('Error initiating open banking:', error);
      miaConsoleLog('error', {
        event: 'payment_handoff_initiate_failed',
        contributionId,
        giftInviteCode: code,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      setPhase('error');
      initiating.current = false;
    }
  }, [
    code,
    contributionId,
    initiateOpenBanking,
    setOpenBankingId,
    setOpenBankingURL,
  ]);

  useEffect(() => {
    if (!canInitiate) return;
    // A payment is already in flight; the redirect below takes over.
    if (inFlightPaymentId) return;
    handoff();
  }, [canInitiate, inFlightPaymentId, handoff]);

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!isHydrated) return null;

  if (!code || !contributionId) {
    return (
      <PaymentScreen
        className='payment-handoff-page'
        heading="We've lost track of your gift"
        body={
          code
            ? 'Your payment was not started. Open your gift link again and we will pick up where you left off.'
            : 'Please open the gift link you were sent to start again.'
        }
        action={
          code ? (
            <Button asChild className='flex w-full'>
              <Link to={`/?code=${code}`}>Start again</Link>
            </Button>
          ) : null
        }
      />
    );
  }

  // The confirmation screen owns a settled gift; it renders from
  // `giftCompleted` alone, with no payment id to hand it.
  if (alreadyPaid) return <Navigate to='/easybanking/success' replace />;

  // ── Handoff ───────────────────────────────────────────────────────────────

  const amountLabel = formatCurrency({ amount, precision: 0 });
  const child = childFirstName?.trim();
  const giftPhrase = child
    ? `your ${amountLabel} gift to ${child}`
    : `your ${amountLabel} gift`;

  const summary = (
    <GiftSummaryChip
      firstName={childFirstName}
      photoUrl={photoSignedUrl}
      showPhoto={showChildPhoto}
      amountLabel={amountLabel}
    />
  );

  if (effectivePhase === 'error') {
    return (
      <PaymentScreen
        className='payment-handoff-page'
        summary={summary}
        mood='asking'
        heading="We couldn't reach your bank"
        body='Nothing has been taken. Please try again — if it keeps happening, use Need help and we will sort it out.'
        action={
          <Button className='block w-full' onClick={handoff}>
            Try again
          </Button>
        }
      />
    );
  }

  // Back from the bank with a payment already in flight. We cannot offer that
  // link again (see the note at the top), so go and find out how it went.
  if (inFlightPaymentId) {
    return (
      <Navigate
        to={`/openbanking/callback?payment_id=${encodeURIComponent(inFlightPaymentId)}`}
        replace
      />
    );
  }

  return (
    <PaymentScreen
        className='payment-handoff-page'
        // AC 2 — mid-transfer to the bank, nothing to interrupt with.
        showHelp={false}
      summary={summary}
      mood='thinking'
      heading='Taking you to your bank…'
      body={`We're handing you over securely to approve ${giftPhrase}.`}
      busy
    />
  );
}

export default PaymentHandoff;
